import React, { useState, useEffect } from "react";
import { useAppContext } from "../utils/context/app-context";
import {
	TextArea,
	TextInput,
	StyledRadioButtons,

	DatepickerInput,
	FormBeforeAfterContent,
	Checkboxes
} from "../components/FormComponents";
import {
	Button,
	ContentContainer,
	TitleHeader,
	NarrowContentWrapper,
	AlertMessage,
	FieldsLoading
} from "../components/UiComponents";
import { CourseHeader, CourseWrapper } from "../components/CourseComponents";
import format from "date-fns/format";
import useDocumentTitle from "../components/useDocumentTitle";
import { useUserIsReadOnly } from "../components/userFunctions";

const TeachingLogPage = () => {
	const { state, api, dispatch } = useAppContext();
	const isOffline = state.offline;
	useDocumentTitle("Teaching Log");
	const [validationErrors, setValidationErrors] = useState(null);
	const [error, setError] = useState(null);
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [formData, setFormData] = useState({
		tutor_attendance: [],
		tutor_attendance_names: [],
		other_tutors: '',
		participants_professional_background: "",
		number_participants_passed: "0",
		number_participants_failed: "0",
		number_participants_na: "0",

		evaluation_of_training_performance: [],
		evaluation_forms_completed: "",
		course_programme_content_available: "",
		injuries: "",
		gsa_notified_of_injuries: "",
		course_comments_tutors_reflective_practice_summary: "",
	});
	const extraContent = state?.forms_extra_content["teaching_log"] || {};
	const required_fields = [
		"participants_professional_background",
		"evaluation_of_training_performance",
		"evaluation_forms_completed",
		"course_programme_content_available",
		"injuries",
		"gsa_notified_of_injuries",
	];
	let course = api.getCurrentCourse();
	let date = api.getCurrentDate();
	const isReadOnly = useUserIsReadOnly();

	useEffect(() => {
		fetchData();
	}, []);

	useEffect(() => {
		if (submitted) {
			setTimeout(() => {
				setSubmitted(false);
			}, 2000);
		}
	}, [submitted]);

	const yesNoOptions = [
		{ value: "yes", label: "Yes" },
		{ value: "no", label: "No" },
	];

	const yesNoNaOptions = [
		{ value: "yes", label: "Yes" },
		{ value: "no", label: "No" },
		{ value: "n/a", label: "N/A" },
	];

	async function fetchData() {
		if (isOffline) {
			setLoading(false);
			return;
		}
		if (date?.teaching_log && typeof date.teaching_log === "object") {
			setFormData(date.teaching_log.answers);
		}
		const { data, error } = await api.fetchData(`/forms/teaching-log/${course.id}/${date.id}`);
		// console.log(data)
		if (!data?.error) {
			if (data?.form) {
				setFormData(data.form.answers);
			}
			setLoading(false);
		} else {
			if (error) {
				setError("Error fetching Teaching Log");
			} else {
				setError("Participant not found");
			}
			setLoading(false);
		}
	}

	function handleFieldChanged(name, value) {
		setFormData({
			...formData,
			[name]: value,
		});
	}

	function handleCheckboxFieldChanged(name, event) {
		const { value } = event.target;
		let newValues = formData[name] || [];
		if (!Array.isArray(newValues)) {
			newValues = [];
		}

		if (newValues.includes(value)) {
			newValues = newValues.filter((val) => val !== value);
		} else {
			newValues.push(value);
		}

		console.log(newValues)

		setFormData({
			...formData,
			[name]: newValues,
		});
	}

	async function handleSubmit(event) {
		event.preventDefault();
		// console.log(formData);
		setValidationErrors(null);
		setError(null);
		setSubmitting(true);
		setSubmitted(false);

		let errors = {};

		for (const [key, value] of Object.entries(formData)) {
			if ((required_fields.includes(key) && !value) || (Array.isArray(value) && value.length === 0)) {
				errors[key] = "This field is required";
			}
		}
		console.log(formData)

		if (Object.keys(errors).length) {
			setValidationErrors(errors);
			setSubmitting(false);
			return;
		}

		let dataToPost = {
			course_id: course.id,
			course_date_id: date.id,
			form_data: { ...formData },
		};

		const { data, error } = await api.fetchData(
			`/forms/teaching-log`,
			"POST",
			dataToPost
		);
		// console.log(data)
		if (data?.success) {
			dispatch({
				type: "SET_COURSE_DATE_TEACHING_LOG",
				payload: {
					course_id: course.id,
					date_id: date.id,
					val: data.log_ids,
				},
			})
			setSubmitting(false);
			setSubmitted(true);
		} else {
			setError(error || data?.error || "An error occurred");
			setSubmitting(false);
		}
	}

	function getFieldValue(name) {
		if (formData[name]) {
			return formData[name];
		}

		return "";
	}

	function getFieldError(name) {
		if (validationErrors && validationErrors[name]) {
			return validationErrors[name];
		}

		return false;
	}

	function handleTutorAttendanceChanged(tutorId, checked) {
		// Handle tutor_attendance and tutor_attendance_names, clone arrays
		let tutorAttendance = [];
		let tutorAttendanceNames = [];

		if (Array.isArray(formData?.tutor_attendance)) {
			tutorAttendance = [...formData.tutor_attendance];
			tutorAttendanceNames = [...formData.tutor_attendance_names];
		}

		// If checked, add tutorId to tutorAttendance and tutorAttendanceNames
		if (checked) {
			tutorAttendance.push(tutorId);
			let tutor = course.tutors.find(tutor => tutor.tutor_id === tutorId);
			tutorAttendanceNames.push(`${tutor.first_name} ${tutor.last_name}`);
		} else {
			// If unchecked, remove tutorId from tutorAttendance and tutorAttendanceNames
			let index = tutorAttendance.indexOf(tutorId);
			if (index > -1) {
				tutorAttendance.splice(index, 1);
				tutorAttendanceNames.splice(index, 1);
			}
		}

		// Make sure both are unique
		tutorAttendance = [...new Set(tutorAttendance)];
		tutorAttendanceNames = [...new Set(tutorAttendanceNames)];

		console.log(tutorAttendance)
		console.log(tutorAttendanceNames)

		setFormData({
			...formData,
			tutor_attendance: tutorAttendance,
			tutor_attendance_names: tutorAttendanceNames,
		});
	}



	function getButtonText() {
		if (submitting) {
			return "Submitting...";
		}

		if (submitted) {
			return "Submitted!";
		}

		return "Submit";
	}

	return (
		<div className="daily-teaching-log-page">
			<CourseHeader />
			<ContentContainer classes="mb-4">
				<CourseWrapper>
					<TitleHeader title="Teaching Log" />
					<NarrowContentWrapper>
						<>
							{error && (
								<div className="mb-3">
									<AlertMessage
										type="danger"
										message={error}
									/>
								</div>
							)}
							{loading ? (
								<FieldsLoading />
							) : (
								<>
									{submitted && (
										<div className="form-submitted mb-3">
											<h3 className="form-submitted__title">Thank you</h3>
											<div className="form-submitted__message">
												<p>Your Teaching Log has been Updated.</p>
											</div>
										</div>
									)}
									<>
										<FormBeforeAfterContent content={extraContent?.before ?? null} />
										<form
											action=""
											className="form teaching-log-form"
											onSubmit={handleSubmit}
											noValidate>
											<div className="form-group">
												<label htmlFor="">
													Tutors in Attendance
												</label>
												<div className="form__section-body">
													{course.tutors.map((tutor) => {
														return (
															<div className="form-check" key={tutor.tutor_id}>
																<label htmlFor={`tutor_attendance_${tutor.tutor_id}`} className="form-check-label">
																	<input
																		id={`tutor_attendance_${tutor.tutor_id}`}
																		type="checkbox"
																		className="form-check-input"
																		name="tutor_attendance"
																		value={tutor.tutor_id}
																		onChange={(e) => {
																			handleTutorAttendanceChanged(
																				tutor.tutor_id,
																				e.target.checked
																			);
																		}}
																		checked={formData?.tutor_attendance ? formData.tutor_attendance.includes(tutor.tutor_id) : false}
																	/>
																	{tutor.first_name} {tutor.last_name}</label>
															</div>
														);
													})}
												</div>
											</div>
											<TextArea
												label="Other Tutors in Attendance"
												name="comments"
												value={
													formData?.other_tutors ? formData.other_tutors : ""
												}
												onChange={(event) => {
													handleFieldChanged(
														"other_tutors",
														event.target.value
													);
												}}
											/>
											<hr />
											<TextInput
												name="participants_professional_background"
												label="Participants Professional Background"
												value={getFieldValue(
													"participants_professional_background"
												)}
												onChange={(e) => {
													handleFieldChanged(
														"participants_professional_background",
														e.target.value
													);
												}}
												required={true}
												error={getFieldError(
													"participants_professional_background"
												)}
											/>
											<label htmlFor="">
												Number of participants which -
											</label>
											<div className="teaching-log-form__participants-fields">
												<TextInput
													name="number_participants_passed"
													label="Passed"
													value={getFieldValue(
														"number_participants_passed"
													)}
													onChange={(e) => {
														handleFieldChanged(
															"number_participants_passed",
															e.target.value
														);
													}}
													required={false}
													error={getFieldError(
														"number_participants_passed"
													)}
													type="number"
												/>
												<TextInput
													name="number_participants_failed"
													label="Failed"
													value={getFieldValue(
														"number_participants_failed"
													)}
													onChange={(e) => {
														handleFieldChanged(
															"number_participants_failed",
															e.target.value
														);
													}}
													required={false}
													error={getFieldError(
														"number_participants_failed"
													)}
													type="number"
												/>
												<TextInput
													name="number_participants_na"
													label="N/A"
													value={getFieldValue(
														"number_participants_na"
													)}
													onChange={(e) => {
														handleFieldChanged(
															"number_participants_na",
															e.target.value
														);
													}}
													required={false}
													error={getFieldError(
														"number_participants_na"
													)}
													type="number"
												/>
											</div>

											<Checkboxes
												label="Evaluation of training programme and/or performance"
												name={"evaluation_of_training_performance"}
												options={[
													{
														label: "Participants",
														value: "participants",
													},
													{
														label: "Self",
														value: "self"
													},
													{
														label: "Peers",
														value: "peers"
													},
												]}
												value={getFieldValue(
													"evaluation_of_training_performance"
												)}
												onChange={(e) => {
													handleCheckboxFieldChanged(
														"evaluation_of_training_performance",
														e
													);
												}}
												required={true}
												error={getFieldError(
													"evaluation_of_training_performance"
												)}
												isCheckboxes={true}
											/>
											<StyledRadioButtons
												label="Evaluation Forms Completed"
												name={"evaluation_forms_completed"}
												options={yesNoOptions}
												value={getFieldValue(
													"evaluation_forms_completed"
												)}
												onChange={(val) => {
													handleFieldChanged(
														"evaluation_forms_completed",
														val
													);
												}}
												required={true}
												error={getFieldError(
													"evaluation_forms_completed"
												)}
											/>
											<StyledRadioButtons
												label="Is a course programme and content available"
												name={"course_programme_content_available"}
												options={yesNoOptions}
												value={getFieldValue(
													"course_programme_content_available"
												)}
												onChange={(val) => {
													handleFieldChanged(
														"course_programme_content_available",
														val
													);
												}}
												required={true}
												error={getFieldError(
													"course_programme_content_available"
												)}
											/>
											<StyledRadioButtons
												label="Any injuries on course? Enter details in Incident Reporting Forms if so"
												name={"injuries"}
												options={yesNoOptions}
												value={getFieldValue("injuries")}
												onChange={(val) => {
													handleFieldChanged("injuries", val);
												}}
												required={true}
												error={getFieldError("injuries")}
											/>
											<StyledRadioButtons
												label="GSA notified of injury"
												name={"gsa_notified_of_injuries"}
												options={yesNoNaOptions}
												value={getFieldValue(
													"gsa_notified_of_injuries"
												)}
												onChange={(val) => {
													handleFieldChanged(
														"gsa_notified_of_injuries",
														val
													);
												}}
												required={true}
												error={getFieldError(
													"gsa_notified_of_injuries"
												)}
											/>
											<div className="form__section-body">
													<TextArea
														label="Course Comments"
														name="course_comments_tutors_reflective_practice_summary"
														rows="10"
														value={
															formData?.course_comments_tutors_reflective_practice_summary
																? formData.course_comments_tutors_reflective_practice_summary
																: ""
														}
														onChange={(event) => {
															handleFieldChanged(
																"course_comments_tutors_reflective_practice_summary",
																event.target.value
															);
														}}
													/>
												</div>
											{validationErrors && (
												<div className="mb-3">
													<AlertMessage
														type="danger"
														message="Please check the form for errors and try again."
													/>
												</div>
											)}
											{error && (
												<div className="mb-3">
													<AlertMessage
														type="danger"
														message={error}
													/>
												</div>
											)}
											<Button
												text={
													getButtonText()
												}
												type="primary"
												disabled={submitting || submitted || isReadOnly || isOffline}
												buttonType="submit"
											/>
										</form>
										<FormBeforeAfterContent content={extraContent?.after ?? null} type="after" />
									</>
								</>
							)}
						</>
					</NarrowContentWrapper>
				</CourseWrapper>
			</ContentContainer>
		</div>
	);
};

export default TeachingLogPage;
